import { useCallback, useEffect, useMemo } from 'react';
import { Checkbox } from '@dynamika-shared/ui/Checkbox/Checkbox';
import { type MenuInfo } from 'rc-menu/lib/interface';
import { CustomTooltip } from '@dynamika-shared/ui/Tooltip/CustomTooltip';
import { useClinicalTrialFiltersSlice } from '@restore/clinicalTrialFilters/clinicalTrialFilters.slice';
import { PossibleFilters, StateFilterItem } from '@restore/clinicalTrialFilters/clinicalTrialFilters.types';
import { StudyStatusEnum } from '@dynamika-shared/types/status';
import { useInventorySlice } from '@restore/inventory/inventory.slice';

export const useClinicalTrialFilters = () => {

  const [sliceState, sliceActions] = useClinicalTrialFiltersSlice((s, a) => [s, a]);

  const {state, selectedFilters, search} = sliceState;

  const {
    toggleActiveFilters,
    toggleSelectedFilters,
    setSearch,
    reset
  } = sliceActions;

  const [inventory] = useInventorySlice(s => [s.inventory]);
  const sponsors = inventory?.sponsors ?? [];

  const sponsorOptions = useMemo(() => {
    const seen = new Set<string>();
    return (sponsors || [])
      .filter(({ name }) => {
        if (seen.has(name)) return false;
        seen.add(name);
        return true;
      })
      .map(s => ({
        label: s.name,
        value: s.id,
      }));
  }, [sponsors]);

  const statusOptions = useMemo(() => {
    return Object.values(StudyStatusEnum).map(st => {
      const label = st
        .split('_')
        .map(w => w.charAt(0) + w.slice(1).toLowerCase())
        .join(' ');
      return { label, value: st };
    });
  }, []);

  const allFilters = useMemo(() => ({
    sponsor: sponsorOptions,
    status:  statusOptions,
  }), [sponsorOptions, statusOptions]);

  const subFilters = useMemo(() => {
    return Object.entries(state) as [PossibleFilters, StateFilterItem][];
  }, [state]);

  const filters = useMemo(() => {
    return [
      {
        key: '2',
        type: 'group' as const,
        label: (
          <div className='text-1216 text-spanishGray'>Set up your Filters</div>
        ),
        children: Object.entries(state).map(([key, filter]) => ({
          key,
          label: (
            <div className='flex justify-between'>
              <CustomTooltip label={filter.label} MAX_LABEL_LENGTH={18} />
              <Checkbox checked={!!state?.[key as PossibleFilters]?.active} />
            </div>
          ),
        })),
      },
    ];
  }, [state]);

  useEffect(() => {
    return () => {
      reset();
    };
  }, []);

  const filterOnClick = useCallback(
    (item: MenuInfo) => toggleActiveFilters(item?.key as PossibleFilters),
    [toggleActiveFilters]
  );

  const selectedSponsorId = selectedFilters.sponsor?.[0];
  const selectedStatus = selectedFilters.status?.[0];

  const args = useMemo(() => ({
    sponsorId: selectedSponsorId,
    status: selectedStatus,
  }), [selectedSponsorId, selectedStatus]);

  return {
    subFilters,
    filters,
    allFilters,
    toggleActiveFilters,
    toggleSelectedFilters,
    selectedFilters,
    filterOnClick,
    args,
    search,
    setSearch,
  };
};
